/**
 * TenantAuthContext — stan zalogowanego usera tenantowego (F7b T7b.1).
 *
 * Różnice wobec admin `AuthContext`:
 *  - Tenant używa `Authorization: Bearer <access>` + refresh cookie (F5 T5.7/T5.9),
 *    admin używa opaque session cookie.
 *  - `/api/auth/me` (T5.10) zwraca `{user, tenant, modules, permissions, companies,
 *    activeCompanyId}` — pełen shell state.
 *  - Login zwraca `{accessToken, expiresIn, user}` lub `{challengeToken, require2fa}`;
 *    2FA obsługiwany jest przez TenantLogin (T7b.2) — context dostaje dopiero finalny
 *    accessToken.
 *
 * Provider mountowany tylko gdy scope === 'tenant'. Automatycznie odświeża `/me`
 * po mount (jeśli refresh cookie jest ważny, request interceptor nie dopnie Bearer
 * — pierwsze /me zwróci 401 → interceptor wywoła refresh → set access token →
 * retry /me → sukces). Dla first-time visitor bez cookie: /me zwróci 401,
 * refresh-queue próbuje /refresh który zwróci 401 NO_REFRESH_COOKIE → clearAccessToken
 * + redirect /login. Consumer widzi user=null.
 *
 * Consumer: `useTenantAuth()` hook — rzuca gdy Provider nie jest w drzewie
 * (błąd developera, nie runtime).
 */

import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { ThemePreset } from '@/contexts/ThemeContext';
import { clearAccessToken, setAccessToken, subscribeAccessToken } from '@/lib/access-token';
import { resolveActiveCompanyId, setStoredActiveCompanyId } from '@/lib/active-company';
import { api } from '@/lib/api';
import { clearDevTenantId } from '@/lib/dev-tenant-id';
import { isTenantUpdatingError } from '@/lib/tenant-updating';
import { buildTenantHandoffUrl } from '@/lib/tenant-url';

export interface TenantUser {
  id: string;
  email: string;
  displayName: string | null;
  isSuperUser: boolean;
  totpEnabled: boolean;
  /** Preferencje UI zapisane na koncie (motyw + tryb jasny/ciemny). */
  uiThemePreset: ThemePreset;
  uiThemeMode: 'light' | 'dark';
}

export interface TenantShortInfo {
  id: string;
  slug: string;
  name: string;
}

export interface TenantModule {
  code: string;
  name: string;
  status: string;
  trialEndsAt: string | null;
  isCore: boolean;
}

export interface TenantCompany {
  id: string;
  name: string;
  /** F9 T9.1: czy jest to firma "domyslna" tenanta (jedna primary per tenant). */
  isPrimary: boolean;
  /** Ścieżka API logo (`/companies/<id>/logo?v=…`, pobierana z tokenem — `useAuthedImage`) lub null. */
  logoUrl: string | null;
}

/** ADR-0075 — firma dostępna dla centralnego konta (przełącznik "Przełącz konto"). */
export interface AvailableTenant {
  tenantId: string;
  slug: string;
  name: string;
  role: string;
  isAdmin: boolean;
  isCurrent: boolean;
  /** ADR-0113b §5: baza firmy w trakcie aktualizacji — w przełączniku nieaktywna z dopiskiem. */
  updating?: boolean;
}

export interface TenantMeResponse {
  user: TenantUser;
  tenant: TenantShortInfo;
  modules: TenantModule[];
  permissions: string[];
  /**
   * F24 menu per user — widoczność menu modułów. `allowed=null` = pełen dostęp (super-user /
   * brak nadpisania); tablica = tylko te kody modułów widoczne. `default` = kod domyślnego menu
   * (jawna gwiazdka) albo null (front bierze pierwsze dostępne).
   */
  menuModules?: { allowed: string[] | null; default: string | null };
  companies: TenantCompany[];
  activeCompanyId: string | null;
  /** ADR-0075 — lista firm centralnego konta (>1 => pokazujemy przełącznik). */
  availableTenants: AvailableTenant[];
  /**
   * F8 T8.5: gdy obecny -> aktualna sesja jest impersonacją wykonywaną przez pracownika
   * platformy ITE. Pole NULL oznacza zwykłą sesję tenant usera. Patrz ADR-0013.
   */
  impersonatedByAdminId: string | null;
  /** F8 T8.5: email administratora podszywającego się (tylko gdy impersonacja). */
  impersonatingAdminEmail: string | null;
  /**
   * F8 T8.5: ISO 8601 timestamp wygasniecia sesji impersonacji (z claim `exp` tokenu).
   * NULL gdy zwykla sesja. Frontend uzywa do countdown w banerze.
   */
  impersonationExpiresAt: string | null;
}

/**
 * Shape zwracany przez `POST /auth/login` i `POST /auth/login/2fa`. User nie zawiera
 * `totpEnabled` — to pole pochodzi z `/me` (source of truth). `setSession` ignoruje
 * `user` (poza typem) — po ustawieniu tokenu wymusza refetch `/me`.
 */
export interface LoginSuccessResponse {
  accessToken: string;
  expiresIn: number;
  user: {
    id: string;
    email: string;
    displayName: string | null;
    isSuperUser: boolean;
  };
}

interface TenantAuthContextValue {
  me: TenantMeResponse | null;
  loading: boolean;
  /** Dopełniony po udanym `POST /login` (no 2FA) — ustawia access token + odświeża /me. */
  setSession: (payload: LoginSuccessResponse) => Promise<void>;
  /** `POST /logout` — unieważnia bieżącą sesję tenanta (refresh revoked), czyści state. */
  logout: () => Promise<void>;
  /** `POST /logout-all` — unieważnia wszystkie sesje użytkownika w tym tenancie. */
  logoutAll: () => Promise<void>;
  /** Ponowne pobranie `/me` (np. po zmianie 2FA status, change-password). */
  refreshMe: () => Promise<void>;
  /**
   * ADR-0075 — przełączenie na inną firmę centralnego konta BEZ ponownego logowania.
   * Woła POST /auth/switch-tenant, dostaje handoff key i przekierowuje na subdomenę
   * firmy (pickupHandoffKey wymienia klucz na sesję). Rzuca gdy brak dostępu.
   */
  switchTenant: (tenantId: string) => Promise<void>;
  /** F8 T8.5: czy aktualna sesja jest impersonacją (admin platformy podszywa się). */
  isImpersonation: boolean;
  /** F8 T8.5: email administratora podszywającego się (null gdy zwykła sesja). */
  impersonatingAdminEmail: string | null;
  /**
   * F8 T8.5: ISO 8601 timestamp wygasniecia sesji impersonacji (z claim `exp` tokenu).
   * NULL gdy zwykla sesja. Banner uzywa do countdown.
   */
  impersonationExpiresAt: string | null;
  /**
   * F8 T8.5: zakończ sesję impersonacji — POST /auth/impersonate/end + clear state.
   * Rzuca gdy backend nie potwierdzil revoke (network/500) — wtedy local state NIE
   * jest czyszczony i banner zostaje, zeby user mogl sprobowac ponownie. Defense vs
   * scenariusz: admin widzi zamknieta karte ale JTI lezy aktywny do 30min.
   */
  endImpersonation: () => Promise<void>;
}

const TenantAuthContext = createContext<TenantAuthContextValue | null>(null);

export const TENANT_ME_QUERY_KEY = ['auth', 'tenant', 'me'] as const;

export function TenantAuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [tokenReady, setTokenReady] = useState(false);

  // Subskrybuj zmiany access tokenu — gdy interceptor wyczyści go (SESSION_REUSED),
  // invaliduj query żeby komponenty re-render-owały jako unauth.
  useEffect(() => {
    // Pierwsze podejście: mount bez tokenu — pozwalamy /me samemu wywołać refresh
    // przez interceptor. setTokenReady(true) pozwala query wystartować.
    setTokenReady(true);
    const unsub = subscribeAccessToken((token) => {
      if (token === null) {
        queryClient.setQueryData(TENANT_ME_QUERY_KEY, null);
      }
    });
    return unsub;
  }, [queryClient]);

  const { data, isLoading, refetch } = useQuery<TenantMeResponse | null>({
    queryKey: TENANT_ME_QUERY_KEY,
    queryFn: async () => {
      try {
        const res = await api.get<TenantMeResponse>('/auth/me');
        // Uzgodnienie aktywnej firmy z aktualna lista `me.companies` przy KAZDYM /me,
        // przed jakimkolwiek query wymagajacym `X-Company-Id` (contractors, employees...).
        // resolveActiveCompanyId honoruje poprawny zapisany id (krok 1 fallback chain),
        // wiec swiadomy wybor usera (CompanySwitcher) zostaje nietkniety. Gdy zapisany id
        // jest niewazny — firma usunieta, utrata UCA, albo drift id po `seed:dev` w dev —
        // podmienia na poprawny ZANIM poleci stale `X-Company-Id` (inaczej 404 na zapisach
        // company-scoped mimo istniejacego rekordu). Wczesniej zapis byl warunkowany pustym
        // localStorage, wiec stary id nigdy sie nie korygowal.
        // Synchronicznie w queryFn (return po setItem) = race-free wzgledem query, ktore
        // czekaja na `me` (ProtectedRoute gating) — czyli strony company-scoped. Query
        // odpalone rownolegle z in-flight `/me` moga jeszcze raz wyslac stary header,
        // ale w praktyce renderuja sie dopiero po rozwiazaniu auth.
        const resolved = resolveActiveCompanyId(res.data.companies, res.data.activeCompanyId);
        if (resolved) setStoredActiveCompanyId(resolved);
        return res.data;
      } catch (err) {
        // ADR-0113b §5: baza firmy w aktualizacji — nie „brak sesji”. Błąd idzie dalej (bez null,
        // więc ProtectedRoute nie przekierowuje na /login); interceptor ustawił już ekran
        // „Trwa aktualizacja systemu”, który zastępuje drzewo tras.
        if (isTenantUpdatingError(err)) throw err;
        // Interceptor już obsłużył (refresh attempt + ew. redirect). Zwracamy null
        // żeby komponent konsumujący renderował fallback (ProtectedRoute → /login).
        return null;
      }
    },
    enabled: tokenReady,
    // /me nie zmienia się w trakcie sesji (wyjątek: zmiana firm, 2FA) — invalidate
    // po akcjach jest jawne. staleTime 5min zapobiega refetchom przy nawigacji.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const value = useMemo<TenantAuthContextValue>(
    () => ({
      me: data ?? null,
      loading: isLoading,
      setSession: async (payload) => {
        setAccessToken(payload.accessToken);
        await queryClient.invalidateQueries({ queryKey: TENANT_ME_QUERY_KEY });
        await refetch();
      },
      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch {
          // nawet jeśli backend failuje — czyścimy lokalny state.
        }
        clearAccessToken();
        // F7b T7b.9 (D24): czyścimy runtime DEV tenant-id żeby re-login od zera nie
        // odziedziczał uuid z sessionStorage po cross-subdomain redirect. W PROD no-op.
        clearDevTenantId();
        // Czyscimy zapamietana aktywna firme — inaczej kolejny user na tym samym
        // origin (ten sam tenant) odziedziczyłby cudzy `active_company_id`.
        setStoredActiveCompanyId(null);
        queryClient.setQueryData(TENANT_ME_QUERY_KEY, null);
      },
      logoutAll: async () => {
        try {
          await api.post('/auth/logout-all');
        } catch {
          // analogicznie — best-effort.
        }
        clearAccessToken();
        clearDevTenantId();
        setStoredActiveCompanyId(null);
        queryClient.setQueryData(TENANT_ME_QUERY_KEY, null);
      },
      refreshMe: async () => {
        await queryClient.invalidateQueries({ queryKey: TENANT_ME_QUERY_KEY });
        await refetch();
      },
      switchTenant: async (tenantId: string) => {
        const res = await api.post<{
          tenant: { tenantId: string; slug: string; name: string };
          handoffKey: string;
        }>('/auth/switch-tenant', { tenantId }, {
          // 503 dotyczy firmy DOCELOWEJ, nie bieżącej — bez globalnego ekranu „Trwa
          // aktualizacja” (przełącznik pokaże komunikat).
          _skipTenantUpdatingGate: true,
        } as never);
        window.location.href = buildTenantHandoffUrl({
          slug: res.data.tenant.slug,
          tenantId: res.data.tenant.tenantId,
          handoffKey: res.data.handoffKey,
        });
      },
      isImpersonation: !!data?.impersonatedByAdminId,
      impersonatingAdminEmail: data?.impersonatingAdminEmail ?? null,
      impersonationExpiresAt: data?.impersonationExpiresAt ?? null,
      endImpersonation: async () => {
        // Server confirmation matters bardziej niz UX smoothness. Jezeli backend nie
        // potwierdzi revoke (network/500), JTI zostaje aktywny do 30min. Rzucamy bys
        // caller (banner) mogl pokazac retry zamiast cicho zamykac karte.
        await api.post('/auth/impersonate/end');
        clearAccessToken();
        clearDevTenantId();
        // Czyscimy DOPIERO po potwierdzonym revoke (powyzszy await rzuca przy bledzie i
        // przerywa) — spojnie z reszta local state, ktorego tez nie czyscimy do potwierdzenia,
        // zeby user mogl ponowic. NIE przenosic przed await (zlamie kontrakt retry).
        setStoredActiveCompanyId(null);
        queryClient.setQueryData(TENANT_ME_QUERY_KEY, null);
        // Po zakończeniu sesji impersonacji typowo zamykamy kartę (otwarta przez admin
        // panel). Jeśli środowisko nie pozwala (window.opener=null) — zostaje pusty stan.
        if (typeof window !== 'undefined') {
          window.close();
        }
      },
    }),
    [data, isLoading, queryClient, refetch],
  );

  return <TenantAuthContext.Provider value={value}>{children}</TenantAuthContext.Provider>;
}

export function useTenantAuth(): TenantAuthContextValue {
  const ctx = useContext(TenantAuthContext);
  if (!ctx) throw new Error('useTenantAuth must be used within TenantAuthProvider');
  return ctx;
}

/** Jak `useTenantAuth`, ale poza TenantAuthProvider (np. panel ITE) zwraca `null` zamiast rzucać —
 *  dla komponentów współdzielonych przez oba scope'y (globalna wyszukiwarka). */
export function useTenantAuthOptional(): TenantAuthContextValue | null {
  return useContext(TenantAuthContext);
}

/**
 * Wartość-atrapa kontekstu: pusta sesja tenanta, funkcje = no-op. Bez sieci
 * (w przeciwieństwie do prawdziwego `TenantAuthProvider`, który odpytuje `/me`).
 */
const PREVIEW_TENANT_AUTH_STUB: TenantAuthContextValue = {
  me: null,
  loading: false,
  setSession: async () => {},
  logout: async () => {},
  logoutAll: async () => {},
  refreshMe: async () => {},
  switchTenant: async () => {},
  isImpersonation: false,
  impersonatingAdminEmail: null,
  impersonationExpiresAt: null,
  endImpersonation: async () => {},
};

/**
 * Granica dla komponentów współdzielonych między scope'ami (np. `ConfigurableTable`),
 * renderowanych w scope admina/ITE — gdzie NIE ma `TenantAuthProvider`. Dostarcza
 * atrapę pustej sesji, żeby `useTenantAuth` nie rzucał. Używane w podglądzie „na żywo"
 * Kreatora u super admina (kartoteka renderowana tymi samymi komponentami co u klienta).
 * NIE robi żadnych zapytań — to czysta atrapa, nie prawdziwy provider.
 */
export function PreviewTenantAuthBoundary({ children }: { children: ReactNode }) {
  return (
    <TenantAuthContext.Provider value={PREVIEW_TENANT_AUTH_STUB}>
      {children}
    </TenantAuthContext.Provider>
  );
}
